import React from 'react';
import { MoreVertical, Square, Sidebar, Layout, ExternalLink, Image, Crop, Edit3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { AspectRatioOption, ViewModeOption } from './types';

interface AssetContextMenuProps {
  aspectRatio: string;
  viewMode: string;
  aspectRatios: AspectRatioOption[];
  availableViewModes: ViewModeOption[];
  onAspectRatioChange: (ratio: string) => void;
  onViewModeChange: (mode: string) => void;
  imageUrls?: {
    primaryOriginal?: string;
    primaryCropped?: string;
    primaryMarkup?: string;
    secondaryOriginal?: string;
    secondaryCropped?: string;
    secondaryMarkup?: string;
  };
}

export const AssetContextMenu: React.FC<AssetContextMenuProps> = ({
  aspectRatio,
  viewMode,
  aspectRatios,
  availableViewModes,
  onAspectRatioChange,
  onViewModeChange,
  imageUrls,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 absolute top-2 right-2 z-10 bg-white/90 hover:bg-white shadow-sm border border-gray-200"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>View Settings</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* View Mode Options */}
        <div className="px-2 py-1">
          <div className="text-xs font-medium text-gray-500 mb-2">View Mode</div>
          <div className="grid grid-cols-3 gap-1">
            {availableViewModes.map((mode) => {
              const Icon = mode.icon;
              return (
                <Button
                  key={mode.value}
                  variant={viewMode === mode.value ? "default" : "ghost"}
                  size="sm"
                  onClick={() => onViewModeChange(mode.value)}
                  className="flex flex-col gap-1 h-auto py-2 text-xs"
                >
                  <Icon className="h-3 w-3" />
                  <span className="text-xs leading-none">{mode.label.split(' ')[0]}</span>
                </Button>
              );
            })}
          </div>
        </div>

        <DropdownMenuSeparator />

        {/* Aspect Ratio Options */}
        <div className="px-2 py-1">
          <div className="text-xs font-medium text-gray-500 mb-2">Aspect Ratio</div>
          <div className="grid grid-cols-3 gap-1">
            {aspectRatios.map((ratio) => (
              <Button
                key={ratio.value}
                variant={aspectRatio === ratio.value ? "default" : "ghost"}
                size="sm"
                onClick={() => onAspectRatioChange(ratio.value)}
                className="text-xs h-auto py-2"
              >
                {ratio.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Links Section */}
        {imageUrls && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1">
              <div className="text-xs font-medium text-gray-500 mb-2">Links</div>
              <div className="space-y-1">
                {/* Primary Image Links */}
                {imageUrls.primaryOriginal && (
                  <DropdownMenuItem asChild>
                    <a
                      href={imageUrls.primaryOriginal}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Image className="h-3 w-3" />
                      <span className="text-xs">Original Primary</span>
                      <ExternalLink className="h-3 w-3 ml-auto" />
                    </a>
                  </DropdownMenuItem>
                )}
                {imageUrls.primaryCropped && (
                  <DropdownMenuItem asChild>
                    <a
                      href={imageUrls.primaryCropped}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Crop className="h-3 w-3" />
                      <span className="text-xs">Cropped Primary</span>
                      <ExternalLink className="h-3 w-3 ml-auto" />
                    </a>
                  </DropdownMenuItem>
                )}
                {imageUrls.primaryMarkup && (
                  <DropdownMenuItem asChild>
                    <a
                      href={imageUrls.primaryMarkup}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Edit3 className="h-3 w-3" />
                      <span className="text-xs">Markup Primary</span>
                      <ExternalLink className="h-3 w-3 ml-auto" />
                    </a>
                  </DropdownMenuItem>
                )}

                {/* Secondary Image Links */}
                {imageUrls.secondaryOriginal && (
                  <DropdownMenuItem asChild>
                    <a
                      href={imageUrls.secondaryOriginal}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Image className="h-3 w-3" />
                      <span className="text-xs">Original Secondary</span>
                      <ExternalLink className="h-3 w-3 ml-auto" />
                    </a>
                  </DropdownMenuItem>
                )}
                {imageUrls.secondaryCropped && (
                  <DropdownMenuItem asChild>
                    <a
                      href={imageUrls.secondaryCropped}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Crop className="h-3 w-3" />
                      <span className="text-xs">Cropped Secondary</span>
                      <ExternalLink className="h-3 w-3 ml-auto" />
                    </a>
                  </DropdownMenuItem>
                )}
                {imageUrls.secondaryMarkup && (
                  <DropdownMenuItem asChild>
                    <a
                      href={imageUrls.secondaryMarkup}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Edit3 className="h-3 w-3" />
                      <span className="text-xs">Markup Secondary</span>
                      <ExternalLink className="h-3 w-3 ml-auto" />
                    </a>
                  </DropdownMenuItem>
                )}
              </div>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};